import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Grid, TrendingUp, Users, Send, Package, Lock, LogOut, RefreshCw, Truck,
  ArrowRight, TrendingDown, ShoppingBag, Activity, Clock, UserX, Mail,
} from 'lucide-react';
import FinancialsTab from '../components/founder/FinancialsTab';
import RevenueChart from '../components/founder/RevenueChart';
import CustomersTab from '../components/founder/CustomersTab';
import InventoryTab from '../components/founder/InventoryTab';
import CampaignsTab from '../components/founder/CampaignsTab';

// Founder's Executive Suite (WEB). A public route with its own Founder-Vault
// unlock gate (founder email + secondary password + dev 2FA). Mirrors the
// mobile /founder tabbed dashboard. Token stored in sessionStorage.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'hg_founder_token';

const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', sage: '#E5EBDD', sageDeep: '#D9E1CB',
  cream: '#FAF6EC', creamWarm: '#F4EEDE', lime: '#B4D330', gold: '#C9A85B',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF',
  border: 'rgba(26,67,49,0.10)', borderStrong: 'rgba(26,67,49,0.18)',
  goldTint: 'rgba(201,168,91,0.14)', leafTint: 'rgba(107,159,74,0.14)',
};
const OF = { fontFamily: 'Outfit, sans-serif' };
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fapi = (token) => axios.create({ baseURL: `${API}/founder`, headers: { 'X-Founder-Token': token } });

const EXEC_TABS = [
  { key: 'home', label: 'Home', Icon: Grid },
  { key: 'financials', label: 'Financials', Icon: TrendingUp },
  { key: 'customers', label: 'Customers', Icon: Users },
  { key: 'campaigns', label: 'Campaigns', Icon: Send },
  { key: 'inventory', label: 'Inventory', Icon: Package },
];

export default function FounderDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [tab, setTab] = useState('home');

  const onAuthErr = useCallback((e) => {
    if (e?.response?.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken('');
      return true;
    }
    return false;
  }, []);

  if (!token) return <VaultGate onUnlock={(t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

  return (
    <div style={{ minHeight: '100vh', background: C.sage }} data-testid="founder-dashboard">
      {/* Command header */}
      <div style={{ background: C.forestDeep, color: C.cream, padding: '20px 24px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', color: C.lime, margin: 0 }}>EXECUTIVE SUITE</p>
            <h1 style={{ ...OF, fontSize: 26, fontWeight: 700, margin: 0 }}>Founder&rsquo;s Dashboard</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(180,211,48,0.15)', borderRadius: 999, padding: '6px 12px' }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: C.lime }} className="animate-pulse" />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.lime }}>LIVE</span>
          </div>
          <button onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(''); }} data-testid="founder-signout"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.6)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogOut className="w-4 h-4" /> Lock
          </button>
        </div>
        {/* Tab rail */}
        <div style={{ maxWidth: 1120, margin: '16px auto 0', display: 'flex', gap: 8, overflowX: 'auto' }}>
          {EXEC_TABS.map(({ key, label, Icon }) => {
            const active = tab === key;
            return (
              <button key={key} onClick={() => setTab(key)} data-testid={`exec-tab-${key}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 999, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', border: 'none', transition: 'all 0.2s', background: active ? C.lime : 'rgba(255,255,255,0.10)', color: active ? C.forestDeep : C.cream }}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '24px' }}>
        {tab === 'home' && <HomeTab token={token} onAuthErr={onAuthErr} />}
        {tab === 'financials' && <FinancialsPane token={token} onAuthErr={onAuthErr} />}
        {tab === 'customers' && <CustomersPane token={token} onAuthErr={onAuthErr} />}
        {tab === 'campaigns' && <CampaignsPane token={token} onAuthErr={onAuthErr} />}
        {tab === 'inventory' && <InventoryPane token={token} onAuthErr={onAuthErr} />}
      </div>
    </div>
  );
}

/* ---------------- Financials pane (fetch + render) ---------------- */
function FinancialsPane({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/finance').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader label="Modeling the P&L…" />;
  if (!data) return <EmptyCard>Financials unavailable — reload to retry.</EmptyCard>;
  return <FinancialsTab data={data} />;
}

/* ---------------- Customers pane ---------------- */
function CustomersPane({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/customers').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader label="Segmenting the community…" />;
  if (!data) return <EmptyCard>Customer data unavailable — reload to retry.</EmptyCard>;
  return <CustomersTab data={data} />;
}

/* ---------------- Inventory pane ---------------- */
function InventoryPane({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/inventory').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader label="Counting the stockroom…" />;
  if (!data) return <EmptyCard>Inventory data unavailable — reload to retry.</EmptyCard>;
  return <InventoryTab data={data} />;
}

/* ---------------- Campaigns pane ---------------- */
function CampaignsPane({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/campaigns').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader label="Measuring marketing ROI…" />;
  if (!data) return <EmptyCard>Campaign data unavailable — reload to retry.</EmptyCard>;
  return <CampaignsTab data={data} token={token} />;
}

/* ---------------- Home tab ---------------- */
function HomeTab({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/dashboard').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader label="Opening the vault…" />;
  if (!data) return <EmptyCard>Dashboard unavailable — reload to retry.</EmptyCard>;

  const v = data.vitals || {};
  const sh = v.subscriber_health || {};
  const gv = v.growth_velocity || {};
  const rev = v.revenue || {};
  const churn = v.churn || {};
  const rt = data.revenue_trend || {};
  const merc = data.mercantile || {};
  const wl = data.waitlist || {};
  const total = sh.total_active || 0;
  const monthlyFlex = Math.max(sh.monthly || 0, 0.001);
  const yearlyFlex = Math.max(sh.yearly || 0, 0.001);

  return (
    <div>
      {/* Data source indicator — live Stripe events only */}
      <div data-testid="founder-data-source" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: (v.events_total || 0) > 0 ? C.leafDeep : C.textMuted }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }}>
          {(v.events_total || 0) > 0
            ? `Live subscription data · ${v.events_total} event${v.events_total === 1 ? '' : 's'}`
            : 'Live data — no subscription activity yet. Metrics appear as members start trials and subscribe.'}
        </span>
      </div>

      {/* Vitals HUD */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <VitalTile label="Active subs" value={`${total}`} tone={C.leafDeep} Icon={Users} />
        <VitalTile label="Net churn" value={`${churn.rate_pct || 0}%`} tone={C.terracotta} Icon={TrendingDown} />
        <VitalTile label="Shop ROI" value={money0(merc.revenue)} tone="#8A6D2B" Icon={ShoppingBag} />
        <VitalTile label="System" value="OK" tone={C.leafDeep} Icon={Activity} />
      </div>

      {/* MRR hero */}
      <div style={{ background: C.forest, borderRadius: 20, padding: 24, marginBottom: 20 }} data-testid="founder-mrr-card">
        <span style={{ fontSize: 12, fontWeight: 600, color: C.lime }}>Monthly recurring revenue</span>
        <p style={{ ...OF, fontSize: 42, lineHeight: '48px', color: C.cream, margin: '4px 0 0', fontWeight: 700 }}>{money2(rev.mrr)}</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <MrrPill label="Projected / yr" value={money0(rev.projected_annual)} />
          <MrrPill label="ARPU" value={money2(rev.arpu)} />
          <MrrPill label="Currency" value={String(rev.currency || 'nzd').toUpperCase()} />
        </div>
      </div>

      {/* MRR trend — real month-by-month MRR */}
      <div style={{ background: C.forestDeep, borderRadius: 20, border: '1px solid rgba(180,211,48,0.2)', padding: 24, marginBottom: 20 }} data-testid="founder-mrr-trend">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.lime }}>MRR TREND · LAST 8 MONTHS</span>
          {rt.mom_growth_pct != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 999, padding: '3px 9px', background: (rt.mom_growth_pct || 0) >= 0 ? 'rgba(107,159,74,0.22)' : 'rgba(198,107,74,0.22)' }}>
              {(rt.mom_growth_pct || 0) >= 0 ? <TrendingUp className="w-3 h-3" style={{ color: C.lime }} /> : <TrendingDown className="w-3 h-3" style={{ color: C.terracotta }} />}
              <span style={{ fontSize: 12, fontWeight: 600, color: (rt.mom_growth_pct || 0) >= 0 ? C.lime : C.terracotta }}>{(rt.mom_growth_pct || 0) >= 0 ? '+' : ''}{rt.mom_growth_pct || 0}% / mo</span>
            </div>
          )}
        </div>
        {rt.has_data
          ? <RevenueChart series={rt.series || []} projection={[]} />
          : <p style={{ fontSize: 13, color: 'rgba(250,246,236,0.6)', margin: '14px 0 0' }}>Your MRR trend will chart here as members start subscribing.</p>}
      </div>

      {/* Supplier Command Center link */}
      <a href="/command" data-testid="founder-supplier-link"
        style={{ display: 'flex', alignItems: 'center', gap: 14, background: C.forestDeep, borderRadius: 18, padding: 16, marginBottom: 20, textDecoration: 'none' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(180,211,48,0.16)' }}><Truck className="w-5 h-5" style={{ color: C.lime }} /></div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: C.cream, margin: 0 }}>Supplier Command Center</p>
          <p style={{ fontSize: 13, color: 'rgba(250,246,236,0.7)', margin: 0 }}>Fulfilment · demand map · partner ROI · AI stocking tips</p>
        </div>
        <ArrowRight className="w-5 h-5" style={{ color: C.lime }} />
      </a>

      {/* Subscriber health */}
      <SectionHeader>Subscriber health</SectionHeader>
      <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20 }} data-testid="founder-subscribers-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ ...OF, fontSize: 38, lineHeight: '44px', color: C.forest, margin: 0, fontWeight: 700 }}>{total}</p>
            <span style={{ fontSize: 13, color: C.textMuted }}>Active subscribers</span>
          </div>
          <Users className="w-10 h-10" style={{ color: C.leaf }} />
        </div>
        <div style={{ display: 'flex', height: 14, marginTop: 20, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ flex: monthlyFlex, background: C.forest }} />
          <div style={{ flex: yearlyFlex, background: C.gold }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, flexWrap: 'wrap', gap: 6 }}>
          <Legend color={C.forest} text={`Monthly · ${sh.monthly || 0} (${sh.pct_monthly || 0}%)`} />
          <Legend color={C.gold} text={`Yearly · ${sh.yearly || 0} (${sh.pct_yearly || 0}%)`} />
        </div>
      </div>

      {/* 30-day movement */}
      <SectionHeader>30-day movement</SectionHeader>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <StatTile Icon={TrendingUp} tone={C.leafDeep} label="Gains" value={`+${gv.gains_30d || 0}`} sub="new & conversions" />
        <StatTile Icon={TrendingDown} tone={C.terracotta} label="Losses" value={`-${gv.losses_30d || 0}`} sub="cancellations" />
        <StatTile Icon={Activity} tone={C.forest} label="Net change" value={`${(gv.net_30d || 0) >= 0 ? '+' : ''}${gv.net_30d || 0}`} sub={`${gv.growth_rate_pct || 0}% vs prior`} />
        <StatTile Icon={Clock} tone="#3F6C97" label="Trials" value={`${gv.trials_30d || 0}`} sub="started this month" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginTop: 12 }}>
        <StatTile Icon={UserX} tone={C.terracotta} label="Churn rate" value={`${churn.rate_pct || 0}%`} sub={`${churn.cancels_30d || 0} in 30 days`} />
        <StatTile Icon={ShoppingBag} tone="#8A6D2B" label="Shop revenue" value={money0(merc.revenue)} sub={`${merc.orders || 0} orders · ${merc.units || 0} units`} />
      </div>

      {/* Top sellers */}
      {merc.top_products && merc.top_products.length > 0 && (
        <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginTop: 20 }} data-testid="founder-top-products">
          <p style={{ ...OF, fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>Top sellers</p>
          {merc.top_products.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
              <span style={{ fontSize: 14, color: C.text }}>{i + 1}. {p.name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, background: C.sageDeep, borderRadius: 999, padding: '3px 10px' }}>{p.units} sold</span>
            </div>
          ))}
        </div>
      )}

      {/* Waitlist */}
      <div style={{ marginTop: 24 }}><SectionHeader>Waitlist demand</SectionHeader></div>
      <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 24, marginBottom: 20 }} data-testid="founder-waitlist-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ ...OF, fontSize: 38, lineHeight: '44px', color: C.forest, margin: 0, fontWeight: 700 }}>{wl.total || 0}</p>
            <span style={{ fontSize: 13, color: C.textMuted }}>On the launch waitlist</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Mail className="w-9 h-9 inline-block" style={{ color: C.gold }} />
            <p style={{ fontSize: 12, fontWeight: 600, color: wl.last_7d ? C.leafDeep : C.textMuted, background: wl.last_7d ? C.leafTint : C.sageDeep, borderRadius: 999, padding: '3px 10px', margin: '6px 0 0' }}>+{wl.last_7d || 0} this week</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Vault gate ---------------- */
function VaultGate({ onUnlock }) {
  const [stage, setStage] = useState('creds');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/founder/request-code`, { email: email.trim(), secondary_password: pw });
      if (r.data?.dev_code) setDevCode(r.data.dev_code);
      setStage('code');
    } catch (e) { setError(e?.response?.data?.detail || 'Could not verify those credentials.'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/founder/verify-code`, { email: email.trim(), code: code.trim() });
      onUnlock(r.data.founder_token);
    } catch (e) { setError(e?.response?.data?.detail || 'Invalid or expired code.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.forestDeep, display: 'grid', placeItems: 'center', padding: 24 }} data-testid="founder-vault-gate">
      <div style={{ width: '100%', maxWidth: 380, background: C.cream, borderRadius: 24, padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: '#1A4331', display: 'grid', placeItems: 'center', marginBottom: 20 }}><Lock className="w-6 h-6" style={{ color: C.lime }} /></div>
        <h1 style={{ ...OF, fontSize: 24, fontWeight: 700, color: C.forest, margin: 0 }}>Founder&rsquo;s Vault</h1>
        <p style={{ fontSize: 14, color: C.textMuted, margin: '4px 0 24px' }}>The Executive Suite is founder-only. Sign in with your vault credentials.</p>

        {stage === 'creds' ? (
          <>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Founder email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" autoComplete="off" data-testid="vault-email"
              style={{ width: '100%', margin: '4px 0 12px', padding: '12px 16px', background: C.white, borderRadius: 12, border: '1px solid rgba(26,67,49,0.1)', outline: 'none', boxSizing: 'border-box' }} />
            <label style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Vault password</label>
            <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" placeholder="••••••••" data-testid="vault-password"
              onKeyDown={(e) => e.key === 'Enter' && email && pw && requestCode()}
              style={{ width: '100%', marginTop: 4, padding: '12px 16px', background: C.white, borderRadius: 12, border: '1px solid rgba(26,67,49,0.1)', outline: 'none', boxSizing: 'border-box' }} />
            {error && <p style={{ fontSize: 14, color: C.terracotta, marginTop: 12 }}>{error}</p>}
            <button onClick={requestCode} disabled={!email || !pw || busy} data-testid="vault-continue"
              style={{ width: '100%', marginTop: 20, background: '#1A4331', color: '#fff', fontWeight: 600, padding: '12px 0', borderRadius: 999, border: 'none', cursor: 'pointer', opacity: (!email || !pw || busy) ? 0.5 : 1 }}>
              {busy ? 'Verifying…' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.text }}>6-digit code</label>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder="••••••" data-testid="vault-code"
              onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && verify()}
              style={{ width: '100%', marginTop: 4, padding: '12px 16px', background: C.white, borderRadius: 12, border: '1px solid rgba(26,67,49,0.1)', letterSpacing: '0.4em', textAlign: 'center', fontSize: 18, outline: 'none', boxSizing: 'border-box' }} />
            {devCode && <p style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }} data-testid="vault-dev-code">Preview code: <b>{devCode}</b></p>}
            {error && <p style={{ fontSize: 14, color: C.terracotta, marginTop: 12 }}>{error}</p>}
            <button onClick={verify} disabled={code.length !== 6 || busy} data-testid="vault-verify"
              style={{ width: '100%', marginTop: 20, background: '#1A4331', color: '#fff', fontWeight: 600, padding: '12px 0', borderRadius: 999, border: 'none', cursor: 'pointer', opacity: (code.length !== 6 || busy) ? 0.5 : 1 }}>
              {busy ? 'Unlocking…' : 'Unlock Executive Suite'}
            </button>
            <button onClick={() => { setStage('creds'); setCode(''); setError(''); }}
              style={{ width: '100%', marginTop: 8, fontSize: 14, color: C.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>Back</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- small shared bits ---------------- */
const Loader = ({ label }) => (
  <div style={{ display: 'grid', placeItems: 'center', padding: '80px 0' }}>
    <div style={{ width: 40, height: 40, border: '4px solid #8EAA97', borderTopColor: '#1A4331', borderRadius: 999 }} className="animate-spin" />
    {label && <p style={{ fontSize: 13, color: C.textMuted, marginTop: 12 }}>{label}</p>}
  </div>
);
const EmptyCard = ({ children }) => (
  <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, padding: 40, textAlign: 'center', color: C.textMuted }}>{children}</div>
);
const SectionHeader = ({ children }) => (
  <p style={{ ...OF, fontSize: 12, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textSoft, margin: '0 0 10px' }}>{children}</p>
);
const MrrPill = ({ label, value }) => (
  <div style={{ flex: 1, background: 'rgba(250,246,236,0.10)', borderRadius: 12, padding: 12 }}>
    <p style={{ fontSize: 12, color: C.cream, margin: 0, opacity: 0.8 }}>{label}</p>
    <p style={{ fontSize: 15, fontWeight: 600, color: C.cream, margin: 0 }}>{value}</p>
  </div>
);
const Legend = ({ color, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 9, height: 9, borderRadius: 999, background: color }} />
    <span style={{ fontSize: 13, color: C.textMuted }}>{text}</span>
  </div>
);
function VitalTile({ label, value, tone, Icon }) {
  return (
    <div style={{ background: C.cream, borderRadius: 14, border: `1px solid ${C.border}`, padding: 12 }} data-testid={`exec-vital-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
      <p style={{ ...OF, fontSize: 19, fontWeight: 700, color: tone, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textSoft, margin: 0 }}>{label}</p>
    </div>
  );
}
function StatTile({ Icon, tone, label, value, sub }) {
  return (
    <div style={{ background: C.cream, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16 }}>
      <Icon className="w-4 h-4" style={{ color: tone }} />
      <p style={{ ...OF, fontSize: 26, fontWeight: 700, color: tone, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 12, color: C.textSoft, margin: 0 }}>{sub}</p>
    </div>
  );
}
function TabPlaceholder({ Icon, title, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '72px 32px', textAlign: 'center' }} data-testid={`exec-placeholder-${title.toLowerCase()}`}>
      <div style={{ width: 56, height: 56, borderRadius: 28, background: 'rgba(26,67,49,0.06)', display: 'grid', placeItems: 'center' }}><Icon className="w-6 h-6" style={{ color: C.forest }} /></div>
      <h3 style={{ ...OF, fontSize: 22, fontWeight: 700, color: C.forest, margin: '16px 0 0' }}>{title}</h3>
      <p style={{ fontSize: 13, color: C.textMuted, margin: '6px 0 0', maxWidth: 420, lineHeight: 1.5 }}>{note}</p>
      <span style={{ marginTop: 12, fontSize: 12, fontWeight: 600, color: C.textMuted, background: C.sageDeep, borderRadius: 999, padding: '4px 12px' }}>Coming this build</span>
    </div>
  );
}
